import type { LitElement } from 'lit'
import type { FieldControlMixinInterface } from './FieldControlMixin.js'
import type { InputMixinInterface } from './InputMixin.js'
import { property } from 'lit/decorators.js'
import { FormDataController } from '../controllers/FormDataController.js'
import { NordEvent } from '../events.js'
import { FieldControlMixin } from './FieldControlMixin.js'

type Constructor<T = Record<string, unknown>> = new (...args: any[]) => T
type NativeInputElement = HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement

export declare class FormAssociatedMixinInterface extends FieldControlMixinInterface {
  placeholder?: string

  protected formData: FormDataController

  protected get formValue(): string | undefined

  protected handleChange(e: Event): void
  protected handleInput(e: Event): void
}

/**
 * Adds form participation (value submission, input/change events) on top of
 * {@link FieldControlMixin}, which provides the built-in field UI and the
 * `<nord-field>` context integration.
 */
export function FormAssociatedMixin<T extends Constructor<InputMixinInterface & LitElement>>(superClass: T) {
  class FormAssociatedElement extends FieldControlMixin(superClass) {
    protected formData = new FormDataController(this, {
      value: () => this.formValue,
      disabled: () => this.effectiveDisabled,
    })

    protected get formValue() {
      return this.value
    }

    /**
     * Placeholder text to display within the control.
     */
    @property({ reflect: true }) placeholder?: string

    protected handleInput(e: Event) {
      e.stopPropagation()
      const target = e.target as NativeInputElement
      this.value = target.value

      /**
       * Fired as the user types into the input.
       */
      this.dispatchEvent(new NordEvent('input'))
    }

    protected handleChange(e: Event) {
      e.stopPropagation()

      /**
       * Fired whenever the input's value is changed via user interaction.
       */
      this.dispatchEvent(new NordEvent('change'))
    }
  }

  return FormAssociatedElement as unknown as Constructor<FormAssociatedMixinInterface> & T
}
